import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Ticket, Plus, X, Loader2, CheckCircle2, XCircle, Clock, Calendar, Users,
  DollarSign, Image as ImageIcon, Instagram, Megaphone, ExternalLink, Phone,
  Mail, FileText, Ban, Play, Square, Pencil, AlertTriangle, Trash2,
} from "lucide-react";
import { eventosPublicosApi, sucursalesApi, EventoPublicoAdmin, PagoPendienteEvento, EstadoEventoPublico } from "../../lib/api";
import { useAuth } from "../../lib/auth";

// Mismo ratio que usan las vistas previas de WhatsApp/Instagram/Facebook (Open
// Graph estándar: 1200x630, 1.91:1) -- así la imagen se ve nítida tanto en el
// link compartido como en la tarjeta del catálogo y el header del detalle.
const IMG_ANCHO_RECOMENDADO = 1200;
const IMG_ALTO_RECOMENDADO = 630;
const IMG_PESO_RECOMENDADO_KB = 500;

const ESTADO_LABEL: Record<EstadoEventoPublico, { txt: string; color: string }> = {
  borrador:  { txt: "Borrador",  color: "bg-ink-100 text-ink-600 border-ink-200" },
  publicado: { txt: "Publicado", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cerrado:   { txt: "Cerrado",   color: "bg-amber-100 text-amber-700 border-amber-200" },
  cancelado: { txt: "Cancelado", color: "bg-rose-100 text-rose-700 border-rose-200" },
};

function fmtUsd(v: string | number | null | undefined) {
  return "$" + Number(v ?? 0).toFixed(2);
}
function fmtFecha(s?: string | null) {
  if (!s) return "—";
  try {
    return new Date(s.slice(0, 10) + "T00:00:00").toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

interface FormEvento {
  titulo: string;
  descripcion: string;
  fechaEvento: string;
  horaInicio: string;
  horaFin: string;
  precioUsd: string;
  capacidadMax: string;
  horasLimitePago: string;
  instruccionesPago: string;
  redSocialUrl: string;
  sucursalId: string;
}

const FORM_VACIO: FormEvento = {
  titulo: "", descripcion: "", fechaEvento: "", horaInicio: "", horaFin: "",
  precioUsd: "", capacidadMax: "", horasLimitePago: "24", instruccionesPago: "",
  redSocialUrl: "", sucursalId: "",
};

export function EventosPublicosPage() {
  const qc = useQueryClient();
  const { token } = useAuth();
  const [tab, setTab] = useState<"eventos" | "pagos">("eventos");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<EventoPublicoAdmin | null>(null);
  const [form, setForm] = useState<FormEvento>(FORM_VACIO);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [avisoImagen, setAvisoImagen] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rechazandoId, setRechazandoId] = useState<number | null>(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");

  const eventos = useQuery({
    queryKey: ["eventos-publicos", "admin"],
    queryFn: () => eventosPublicosApi.listarAdmin(),
    refetchInterval: 30_000,
  });

  const resumen = useQuery({
    queryKey: ["eventos-publicos", "resumen"],
    queryFn: () => eventosPublicosApi.resumen(),
  });

  const pagos = useQuery({
    queryKey: ["eventos-publicos", "pagos-pendientes"],
    queryFn: () => eventosPublicosApi.pagosPendientes(),
    enabled: tab === "pagos",
    refetchInterval: 30_000,
  });

  const sucursales = useQuery({ queryKey: ["sucursales", "list"], queryFn: () => sucursalesApi.list() });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["eventos-publicos"] });
  };

  const publicar = useMutation({ mutationFn: (id: number) => eventosPublicosApi.publicar(id), onSuccess: invalidar });
  const cerrar = useMutation({ mutationFn: (id: number) => eventosPublicosApi.cerrar(id), onSuccess: invalidar });
  const cancelar = useMutation({
    mutationFn: (id: number) => eventosPublicosApi.cancelar(id),
    onSuccess: invalidar,
  });
  const eliminar = useMutation({
    mutationFn: (id: number) => eventosPublicosApi.eliminar(id),
    onSuccess: invalidar,
    onError: (e: any) => alert(e?.message ?? "No se pudo eliminar el evento"),
  });
  const anunciar = useMutation({
    mutationFn: (id: number) => eventosPublicosApi.anunciarWhatsapp(id),
    onSuccess: (r) => { invalidar(); alert(`Anuncio enviado a ${r.enviados} cliente(s).`); },
  });

  const aprobarPago = useMutation({ mutationFn: (id: number) => eventosPublicosApi.aprobarPago(id), onSuccess: invalidar });
  const rechazarPago = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => eventosPublicosApi.rechazarPago(id, motivo),
    onSuccess: () => { invalidar(); setRechazandoId(null); setMotivoRechazo(""); },
  });

  const abrirNuevo = () => {
    setEditando(null);
    setForm(FORM_VACIO);
    setArchivo(null); setPreview(null); setAvisoImagen(null);
    setError(null);
    setModalAbierto(true);
  };

  const abrirEditar = (ev: EventoPublicoAdmin) => {
    setEditando(ev);
    setForm({
      titulo: ev.titulo,
      descripcion: ev.descripcion ?? "",
      fechaEvento: ev.fecha_evento?.slice(0, 10) ?? "",
      horaInicio: ev.hora_inicio?.slice(0, 5) ?? "",
      horaFin: ev.hora_fin?.slice(0, 5) ?? "",
      precioUsd: String(ev.precio_usd ?? ""),
      capacidadMax: String(ev.capacidad_max ?? ""),
      horasLimitePago: String(ev.horas_limite_pago ?? 24),
      instruccionesPago: ev.instrucciones_pago ?? "",
      redSocialUrl: ev.red_social_url ?? "",
      sucursalId: ev.sucursal_id ? String(ev.sucursal_id) : "",
    });
    setArchivo(null); setPreview(null); setAvisoImagen(null);
    setError(null);
    setModalAbierto(true);
  };

  const seleccionarArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { setError("La imagen no puede superar 20MB"); return; }
    setArchivo(f);
    setAvisoImagen(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setPreview(dataUrl);

      const img = new Image();
      img.onload = () => {
        const avisos: string[] = [];
        if (img.width < IMG_ANCHO_RECOMENDADO || img.height < IMG_ALTO_RECOMENDADO) {
          avisos.push(`se ve pixelada: subió ${img.width}×${img.height}px, lo mínimo recomendado es ${IMG_ANCHO_RECOMENDADO}×${IMG_ALTO_RECOMENDADO}px`);
        }
        const ratio = img.width / img.height;
        const ratioRecomendado = IMG_ANCHO_RECOMENDADO / IMG_ALTO_RECOMENDADO;
        if (ratio < ratioRecomendado * 0.75 || ratio > ratioRecomendado * 1.25) {
          avisos.push(`no es horizontal: se recomienda una proporción cercana a ${ratioRecomendado.toFixed(2)}:1 (apaisada), la que subiste es ${ratio.toFixed(2)}:1`);
        }
        if (f.size > IMG_PESO_RECOMENDADO_KB * 1024) {
          avisos.push(`pesa ${(f.size / 1024 / 1024).toFixed(1)}MB — comprimila a menos de ${IMG_PESO_RECOMENDADO_KB}KB para que cargue rápido en el link público`);
        }
        if (avisos.length > 0) setAvisoImagen(avisos.join("; "));
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(f);
  };

  const guardar = async () => {
    if (!form.titulo.trim()) { setError("El título es obligatorio"); return; }
    if (!form.fechaEvento) { setError("La fecha es obligatoria"); return; }
    if (!form.precioUsd || Number(form.precioUsd) < 0) { setError("Precio inválido"); return; }
    if (!form.capacidadMax || Number(form.capacidadMax) <= 0) { setError("Capacidad inválida"); return; }

    setGuardando(true); setError(null);
    try {
      const dto = {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim() || undefined,
        fechaEvento: form.fechaEvento,
        horaInicio: form.horaInicio || undefined,
        horaFin: form.horaFin || undefined,
        precioUsd: Number(form.precioUsd),
        capacidadMax: Number(form.capacidadMax),
        horasLimitePago: Number(form.horasLimitePago) || 24,
        instruccionesPago: form.instruccionesPago.trim() || undefined,
        redSocialUrl: form.redSocialUrl.trim() || undefined,
        sucursalId: form.sucursalId ? Number(form.sucursalId) : undefined,
      };

      let eventoId: number;
      if (editando) {
        await eventosPublicosApi.editar(editando.id, dto);
        eventoId = editando.id;
      } else {
        const creado = await eventosPublicosApi.crear(dto);
        eventoId = creado.id;
      }

      if (archivo) {
        const fd = new FormData();
        fd.append("file", archivo);
        await fetch(`/api/v1/admin/eventos-publicos/${eventoId}/imagen`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        }).catch(() => null);
        // No bloqueamos el guardado del evento si falla solo la imagen
      }

      invalidar();
      setModalAbierto(false);
    } catch (e: any) {
      setError(e?.message ?? "Error al guardar el evento");
    } finally {
      setGuardando(false);
    }
  };

  const items = eventos.data ?? [];
  const pagosItems = pagos.data ?? [];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink-900 flex items-center gap-3">
            <Ticket className="size-7 text-violet-500" />
            Eventos Públicos
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Vendé entradas con cupo limitado. El cupo se reserva desde la compra hasta que se verifica el pago.
          </p>
        </div>
        <button onClick={abrirNuevo}
          className="px-4 py-2.5 rounded-xl bg-violet-600 text-white font-semibold text-sm hover:bg-violet-700 transition inline-flex items-center gap-2 shadow-sm shrink-0">
          <Plus className="size-4" /> Nuevo evento
        </button>
      </header>

      {resumen.data && (
        <div className="grid grid-cols-2 tab:grid-cols-4 gap-3">
          <Stat label="Publicados" value={resumen.data.eventosPublicados} color="text-violet-700" icon={Ticket} />
          <Stat label="Entradas confirmadas" value={resumen.data.entradasConfirmadas} color="text-emerald-700" icon={Users} />
          <Stat label="Cobrado total" value={fmtUsd(resumen.data.totalCobradoUsd)} color="text-emerald-700" icon={DollarSign} />
          <Stat label="Pagos pendientes" value={resumen.data.pagosPendientes} color="text-amber-700" icon={Clock} highlight={resumen.data.pagosPendientes > 0} />
        </div>
      )}

      <div className="flex border-b border-ink-200">
        {[
          { key: "eventos", label: "Eventos" },
          { key: "pagos", label: `Pagos pendientes${pagosItems.length ? ` (${pagosItems.length})` : ""}` },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              tab === t.key ? "border-violet-500 text-violet-700" : "border-transparent text-ink-500 hover:text-ink-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "eventos" && (
        eventos.isLoading ? (
          <div className="text-center py-12 text-ink-400"><Loader2 className="size-6 animate-spin mx-auto mb-2" /> Cargando…</div>
        ) : items.length === 0 ? (
          <div className="card p-12 text-center">
            <Ticket className="size-10 text-ink-300 mx-auto mb-2" />
            <p className="font-medium text-ink-800">Todavía no creaste ningún evento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 tab:grid-cols-2 gap-4">
            {items.map((ev) => {
              const est = ESTADO_LABEL[ev.estado];
              const vendidas = ev.entradasVendidas ?? 0;
              return (
                <article key={ev.id} className="card overflow-hidden flex">
                  <div className="w-28 shrink-0 bg-ink-100 relative">
                    {ev.imagen_url ? (
                      <img src={ev.imagen_url} alt={ev.titulo} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-ink-300"><ImageIcon className="size-8" /></div>
                    )}
                  </div>
                  <div className="flex-1 p-4 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-ink-900 truncate">{ev.titulo}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold shrink-0 ${est.color}`}>{est.txt}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-ink-600">
                      <span><Calendar className="inline size-3" /> {fmtFecha(ev.fecha_evento)}</span>
                      <span><DollarSign className="inline size-3" /> {fmtUsd(ev.precio_usd)}</span>
                      <span><Users className="inline size-3" /> {vendidas}/{ev.capacidad_max}</span>
                    </div>
                    {ev.red_social_url && (
                      <a href={ev.red_social_url} target="_blank" rel="noopener"
                        className="inline-flex items-center gap-1 text-xs text-pink-600 hover:underline mt-1.5">
                        <Instagram className="size-3" /> Post original <ExternalLink className="size-3" />
                      </a>
                    )}

                    <div className="flex flex-wrap gap-2 mt-3">
                      <button onClick={() => abrirEditar(ev)}
                        className="px-2.5 py-1.5 text-xs rounded-lg border border-ink-300 hover:bg-ink-100 inline-flex items-center gap-1">
                        <Pencil className="size-3" /> Editar
                      </button>
                      {ev.estado === "borrador" && (
                        <button onClick={() => publicar.mutate(ev.id)} disabled={publicar.isPending}
                          className="px-2.5 py-1.5 text-xs rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-50">
                          <Play className="size-3" /> Publicar
                        </button>
                      )}
                      {ev.estado === "publicado" && (
                        <>
                          <a href={`/eventos/${ev.id}`} target="_blank" rel="noopener"
                            className="px-2.5 py-1.5 text-xs rounded-lg border border-ink-300 hover:bg-ink-100 inline-flex items-center gap-1">
                            <ExternalLink className="size-3" /> Ver página
                          </a>
                          <button onClick={() => { if (confirm(`¿Anunciar "${ev.titulo}" por WhatsApp a todos los clientes con opt-in de marketing?`)) anunciar.mutate(ev.id); }}
                            disabled={anunciar.isPending}
                            className="px-2.5 py-1.5 text-xs rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 inline-flex items-center gap-1 disabled:opacity-50">
                            {anunciar.isPending ? <Loader2 className="size-3 animate-spin" /> : <Megaphone className="size-3" />} Anunciar
                          </button>
                          <button onClick={() => cerrar.mutate(ev.id)} disabled={cerrar.isPending}
                            className="px-2.5 py-1.5 text-xs rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1">
                            <Square className="size-3" /> Cerrar
                          </button>
                        </>
                      )}
                      {(ev.estado === "borrador" || ev.estado === "publicado" || ev.estado === "cerrado") && (
                        <button onClick={() => { if (confirm("¿Cancelar este evento? Se avisará a los compradores.")) cancelar.mutate(ev.id); }}
                          disabled={cancelar.isPending}
                          className="px-2.5 py-1.5 text-xs rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                          <Ban className="size-3" /> Cancelar
                        </button>
                      )}
                      <button onClick={() => { if (confirm(`¿Eliminar "${ev.titulo}" definitivamente? Esta acción no se puede deshacer. Si ya tiene compras asociadas, no se va a poder eliminar — usá "Cancelar" en ese caso.`)) eliminar.mutate(ev.id); }}
                        disabled={eliminar.isPending}
                        className="px-2.5 py-1.5 text-xs rounded-lg border border-ink-300 text-ink-500 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 inline-flex items-center gap-1 ml-auto">
                        <Trash2 className="size-3" /> Eliminar
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )
      )}

      {tab === "pagos" && (
        pagos.isLoading ? (
          <div className="text-center py-12 text-ink-400"><Loader2 className="size-6 animate-spin mx-auto mb-2" /> Cargando…</div>
        ) : pagosItems.length === 0 ? (
          <div className="card p-12 text-center">
            <CheckCircle2 className="size-10 text-emerald-300 mx-auto mb-2" />
            <p className="font-medium text-ink-800">No hay pagos pendientes de verificar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pagosItems.map((p: PagoPendienteEvento) => (
              <article key={p.id} className="card overflow-hidden">
                <div className="px-4 py-2.5 border-b border-ink-100 bg-ink-50/40 flex items-center gap-2">
                  <Clock className="size-4 text-amber-500" />
                  <span className="text-sm font-semibold text-ink-800">{p.eventoTitulo}</span>
                  <span className="text-xs text-ink-500 ml-auto">reportado: {new Date(p.createdAt).toLocaleString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className="p-4 grid grid-cols-1 tab:grid-cols-3 gap-4">
                  <div className="tab:col-span-2 space-y-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Pagador</p>
                      <p className="font-semibold text-ink-800">{p.nombrePagador}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-ink-600">
                        {p.telefono && <a href={`tel:${p.telefono}`} className="hover:text-ink-800"><Phone className="inline size-3" /> {p.telefono}</a>}
                        {p.email && <a href={`mailto:${p.email}`} className="hover:text-ink-800 truncate max-w-[200px]"><Mail className="inline size-3" /> {p.email}</a>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Monto reportado</p>
                        <p className="font-bold text-emerald-700 text-lg">{p.moneda !== "USD" ? `${p.moneda} ${Number(p.montoUsd).toFixed(2)}` : fmtUsd(p.montoUsd)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Entradas / total</p>
                        <p className="text-sm text-ink-700">{p.cantidadEntradas} — {fmtUsd(p.montoTotalUsd)}</p>
                      </div>
                      {p.banco && <div><p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Banco</p><p className="text-sm text-ink-700">{p.banco}</p></div>}
                      {p.numeroReferencia && <div><p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold">Referencia</p><p className="text-sm text-ink-700 font-mono">{p.numeroReferencia}</p></div>}
                    </div>
                    {p.comprobanteUrl && (
                      <div className="mt-2">
                        {p.comprobanteUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                          <a href={p.comprobanteUrl} target="_blank" rel="noopener">
                            <img src={p.comprobanteUrl} alt="Comprobante" className="max-h-40 rounded-lg border border-ink-200 object-contain hover:opacity-80 transition" />
                          </a>
                        ) : (
                          <a href={p.comprobanteUrl} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                            <FileText className="size-3" /> Ver comprobante <ExternalLink className="size-3" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="border-t tab:border-t-0 tab:border-l border-ink-100 pt-3 tab:pt-0 tab:pl-4">
                    <p className="text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">Evento</p>
                    <p className="text-sm text-ink-800">{p.eventoTitulo}</p>
                    <p className="text-xs text-ink-500 mt-1"><Calendar className="inline size-3" /> {fmtFecha(p.fechaEvento)}</p>
                  </div>
                </div>
                <div className="px-4 py-3 bg-ink-50 border-t border-ink-100">
                  {rechazandoId === p.id ? (
                    <div className="space-y-2">
                      <textarea value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)}
                        rows={2} placeholder="Motivo del rechazo (el cliente lo verá)…"
                        className="w-full text-sm border border-ink-300 rounded-lg px-3 py-2" />
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => { setRechazandoId(null); setMotivoRechazo(""); }}
                          className="px-3 py-1.5 text-sm rounded-lg border border-ink-300 hover:bg-ink-100">Cancelar</button>
                        <button disabled={!motivoRechazo.trim() || rechazarPago.isPending}
                          onClick={() => rechazarPago.mutate({ id: p.id, motivo: motivoRechazo })}
                          className="px-3 py-1.5 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
                          Confirmar rechazo
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setRechazandoId(p.id)}
                        className="px-4 py-2 text-sm rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                        <XCircle className="size-4" /> Rechazar
                      </button>
                      <button onClick={() => aprobarPago.mutate(p.id)} disabled={aprobarPago.isPending}
                        className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                        {aprobarPago.isPending && <Loader2 className="size-4 animate-spin" />}
                        <CheckCircle2 className="size-4" /> Aprobar y confirmar entrada
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )
      )}

      {/* Modal crear/editar evento */}
      {modalAbierto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setModalAbierto(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-ink-200 px-5 py-4 rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="font-display text-lg font-bold text-ink-900 flex items-center gap-2">
                <Ticket className="size-5 text-violet-600" /> {editando ? "Editar evento" : "Nuevo evento"}
              </h2>
              <button onClick={() => setModalAbierto(false)} className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-500"><X className="size-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              <Campo label="Título *">
                <input className="input" value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} />
              </Campo>

              <Campo label="Descripción">
                <textarea className="input" rows={3} value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} />
              </Campo>

              <Campo label="Imagen de portada">
                <p className="text-xs text-ink-500 mb-1.5">
                  Para el diseñador: <strong>{IMG_ANCHO_RECOMENDADO}×{IMG_ALTO_RECOMENDADO}px</strong> (horizontal,
                  proporción 1.91:1), formato JPG o WEBP, menos de <strong>{IMG_PESO_RECOMENDADO_KB}KB</strong>.
                  Es la misma proporción que usan las vistas previas de WhatsApp/Instagram, así se ve nítida
                  tanto en el link compartido como en la tarjeta del catálogo.
                </p>
                <input ref={fileRef} type="file" accept="image/*" onChange={seleccionarArchivo} className="input" style={{ padding: "8px 10px" }} />
                {avisoImagen && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-1.5 inline-flex items-start gap-1.5">
                    <AlertTriangle className="size-3.5 shrink-0 mt-0.5" /> Esta imagen {avisoImagen}.
                  </p>
                )}
                {(preview || editando?.imagen_url) && (
                  <img src={preview ?? editando?.imagen_url ?? undefined} alt="Vista previa"
                    className="mt-2 max-h-40 rounded-lg border border-ink-200 object-cover" />
                )}
              </Campo>

              <Campo label="Link al post original (Instagram, Facebook, etc.)">
                <input className="input" value={form.redSocialUrl} onChange={(e) => setForm((f) => ({ ...f, redSocialUrl: e.target.value }))} placeholder="https://instagram.com/p/..." />
              </Campo>

              <div className="grid grid-cols-2 gap-3">
                <Campo label="Fecha *">
                  <input type="date" className="input" value={form.fechaEvento} onChange={(e) => setForm((f) => ({ ...f, fechaEvento: e.target.value }))} />
                </Campo>
                <Campo label="Sucursal (opcional)">
                  <select className="input" value={form.sucursalId} onChange={(e) => setForm((f) => ({ ...f, sucursalId: e.target.value }))}>
                    <option value="">Todas / sin especificar</option>
                    {(sucursales.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </Campo>
                <Campo label="Hora inicio">
                  <input type="time" className="input" value={form.horaInicio} onChange={(e) => setForm((f) => ({ ...f, horaInicio: e.target.value }))} />
                </Campo>
                <Campo label="Hora fin">
                  <input type="time" className="input" value={form.horaFin} onChange={(e) => setForm((f) => ({ ...f, horaFin: e.target.value }))} />
                </Campo>
                <Campo label="Precio por entrada (USD) *">
                  <input type="number" min="0" step="0.01" className="input" value={form.precioUsd} onChange={(e) => setForm((f) => ({ ...f, precioUsd: e.target.value }))} />
                </Campo>
                <Campo label="Capacidad máxima (entradas) *">
                  <input type="number" min="1" className="input" value={form.capacidadMax} onChange={(e) => setForm((f) => ({ ...f, capacidadMax: e.target.value }))} />
                </Campo>
                <Campo label="Horas límite para reportar el pago">
                  <input type="number" min="1" className="input" value={form.horasLimitePago} onChange={(e) => setForm((f) => ({ ...f, horasLimitePago: e.target.value }))} />
                </Campo>
              </div>

              <Campo label="Instrucciones de pago (se muestran al comprador)">
                <textarea className="input" rows={2} value={form.instruccionesPago} onChange={(e) => setForm((f) => ({ ...f, instruccionesPago: e.target.value }))}
                  placeholder="Ej: Pago móvil al 0412-1234567, Banco Mercantil, RIF J-12345678-9" />
              </Campo>

              {error && <div className="bg-rose-50 text-rose-700 text-sm rounded-lg p-3">{error}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModalAbierto(false)} className="px-4 py-2 text-sm rounded-lg border border-ink-300 hover:bg-ink-100">Cancelar</button>
                <button onClick={guardar} disabled={guardando}
                  className="px-4 py-2 text-sm rounded-lg bg-violet-600 text-white font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-2">
                  {guardando && <Loader2 className="size-4 animate-spin" />} {editando ? "Guardar cambios" : "Crear evento"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.input { width: 100%; padding: 9px 11px; border-radius: 10px; border: 1.5px solid #d1d5db; font-size: 14px; } .input:focus { outline: 2px solid rgba(124,58,237,0.3); border-color: #7c3aed; }`}</style>
    </div>
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value, color, icon: Icon, highlight }: {
  label: string; value: any; color?: string; icon: any; highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-3 text-center ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-ink-200"}`}>
      <Icon className={`size-5 mx-auto mb-1 ${color}`} />
      <div className={`font-bold text-xl ${color}`}>{value}</div>
      <div className="text-[11px] text-ink-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
